import { gql, useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { ServiceProduct } from "../service-products/types";
import {
  CreateServiceProduct,
  CreateServiceProductVariables
} from "./schema/CreateServiceProduct";
import {
  DeactivateSolutionServiceProduct,
  DeactivateSolutionServiceProductVariables
} from "./schema/DeactivateSolutionServiceProduct";
import {
  SolutionServiceProduct,
  SolutionServiceProduct_solutions_solution_useCases_serviceProducts_serviceProduct,
  SolutionServiceProductVariables
} from "./schema/SolutionServiceProduct";
import {
  SolutionServiceProducts,
  SolutionServiceProductsVariables
} from "./schema/SolutionServiceProducts";
import {
  UpdateNotificationTriggers,
  UpdateNotificationTriggersVariables
} from "./schema/UpdateNotificationTriggers";

const GET_SERVICE_PRODUCT = gql`
  query SolutionServiceProduct($solutionKey: Key!, $serviceProductId: ID!) {
    solutions {
      solution(solutionKey: $solutionKey) {
        useCases {
          serviceProducts {
            serviceProduct(serviceProductId: $serviceProductId) {
              apiKeyId
              apiKey {
                id
                name
              }
              details {
                allPeriods {
                  endDate
                  startDate
                  usedTransactionCount
                }
                currentPeriod {
                  endDate
                  startDate
                  usedTransactionCount
                }
                currentPeriodNumber
                endDate
                periodCount
                startDate
                status
              }
              id
              limitType
              name
              services
              transactionLimit
              notificationTriggers {
                email
                thresholds
              }
              notificationDeliveryStatuses {
                deliveryStatus
                email
                error
                periodNumber
                thresholdValue
                triggeredDate
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_SERVICE_PRODUCTS = gql`
  query SolutionServiceProducts($solutionKey: Key!) {
    solutions {
      solution(solutionKey: $solutionKey) {
        useCases {
          serviceProducts {
            serviceProducts {
              id
              limitType
              name
              apiKey {
                id
                name
              }
              details {
                status
                currentPeriodNumber
                currentPeriod {
                  endDate
                  startDate
                  usedTransactionCount
                }
                allPeriods {
                  endDate
                  startDate
                  usedTransactionCount
                }
                periodCount
                startDate
                endDate
              }
              apiKeyId
              services
              transactionLimit
              notificationTriggers {
                email
                thresholds
              }
              notificationDeliveryStatuses {
                deliveryStatus
                email
                error
                periodNumber
                thresholdValue
                triggeredDate
              }
            }
          }
        }
      }
    }
  }
`;

const CREATE_SERVICE_PRODUCT = gql`
  mutation CreateServiceProduct(
    $solutionKey: Key!
    $apiKeyId: String!
    $limitType: LimitType!
    $name: String!
    $periods: [ServiceProductPeriodInputInput!]!
    $services: [Key!]!
    $transactionLimit: Int!
    $notificationTriggers: [NotificationTriggerInput!]!
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        useCases {
          serviceProducts {
            create(
              apiKeyId: $apiKeyId
              periods: $periods
              limitType: $limitType
              name: $name
              transactionLimit: $transactionLimit
              services: $services
              notificationTriggers: $notificationTriggers
            ) {
              apiKeyId
              apiKey {
                id
                name
              }
              details {
                allPeriods {
                  endDate
                  startDate
                  usedTransactionCount
                }
                currentPeriod {
                  endDate
                  startDate
                  usedTransactionCount
                }
                currentPeriodNumber
                endDate
                periodCount
                startDate
                status
              }
              id
              limitType
              name
              services
              transactionLimit
              notificationTriggers {
                email
                thresholds
              }
              notificationDeliveryStatuses {
                deliveryStatus
                email
                error
                periodNumber
                thresholdValue
                triggeredDate
              }
            }
          }
        }
      }
    }
  }
`;

const UPDATE_NOTIFICATION_TRIGGERS = gql`
  mutation UpdateNotificationTriggers(
    $notificationTriggers: [NotificationTriggerInput!]!
    $serviceProductId: ID!
    $solutionKey: Key!
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        useCases {
          serviceProducts {
            updateNotificationTriggers(
              notificationTriggers: $notificationTriggers
              serviceProductId: $serviceProductId
            ) {
              id
              notificationTriggers {
                email
                thresholds
              }
              notificationDeliveryStatuses {
                deliveryStatus
                email
                error
                periodNumber
                thresholdValue
                triggeredDate
              }
            }
          }
        }
      }
    }
  }
`;

const DEACTIVATE_SERVICE_PRODUCT = gql`
  mutation DeactivateSolutionServiceProduct(
    $solutionKey: Key!
    $serviceProductId: ID!
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        useCases {
          serviceProducts {
            deactivate(serviceProductId: $serviceProductId) {
              details {
                status
              }
              id
            }
          }
        }
      }
    }
  }
`;

export const useServiceProduct = (
  solutionKey: string,
  serviceProductId: string
) => {
  const [serviceProduct, setServiceProduct] = useState<ServiceProduct | null>(
    null
  );
  const { loading, error, data } = useQuery<
    SolutionServiceProduct,
    SolutionServiceProductVariables
  >(GET_SERVICE_PRODUCT, {
    variables: { solutionKey, serviceProductId }
  });

  useEffect(() => {
    if (data) {
      const product =
        data.solutions.solution?.useCases?.serviceProducts?.serviceProduct;
      setServiceProduct(product ? mapServiceProduct(product) : null);
    }
  }, [data]);

  return { serviceProduct, loading, error };
};

export const useServiceProducts = (solutionKey: string) => {
  const { loading, error, data } = useQuery<
    SolutionServiceProducts,
    SolutionServiceProductsVariables
  >(GET_SERVICE_PRODUCTS, {
    variables: { solutionKey },
    fetchPolicy: "no-cache"
  });

  const serviceProducts: ServiceProduct[] =
    data?.solutions.solution?.useCases?.serviceProducts?.serviceProducts?.map(
      mapServiceProduct
    ) || [];

  return { serviceProducts, loading, error };
};

export const useCreateServiceProduct = () => {
  return useMutation<CreateServiceProduct, CreateServiceProductVariables>(
    CREATE_SERVICE_PRODUCT
  );
};

export const useDeactivateServiceProduct = () => {
  return useMutation<
    DeactivateSolutionServiceProduct,
    DeactivateSolutionServiceProductVariables
  >(DEACTIVATE_SERVICE_PRODUCT);
};

export const useUpdateNotificationTriggers = () => {
  return useMutation<
    UpdateNotificationTriggers,
    UpdateNotificationTriggersVariables
  >(UPDATE_NOTIFICATION_TRIGGERS);
};
function mapServiceProduct(
  product: SolutionServiceProduct_solutions_solution_useCases_serviceProducts_serviceProduct
): ServiceProduct {
  return {
    id: product.id,
    name: product.name,
    transactionLimit: product.transactionLimit,
    limitType: product.limitType,
    services: product.services,
    apiKeyId: product.apiKeyId,
    apiKey: product.apiKey,
    startDate: product.details.startDate,
    endDate: product.details.endDate,
    periods: product.details.allPeriods,
    currentPeriod: product.details.currentPeriod,
    periodCount: product.details.periodCount,
    currentPeriodNumber: product.details.currentPeriodNumber,
    status: product.details.status,
    notificationTriggers: product.notificationTriggers,
    notificationDeliveryStatuses: product.notificationDeliveryStatuses
  };
}
